import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce, DebouncedFunc } from "lodash";
import { MpxContext } from "@app/provider";
import { MpxAddressSuggestion, GeocodedAddress } from "@app/service/mpx";

const DEBOUNCE_DELAY = 300;

export function useAddressAutocomplete() {
  const [results, setResults] = useState<MpxAddressSuggestion[]>([]);
  const [selectedAddress, setSelectedAddress] =
    useState<GeocodedAddress | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [lastSuggestReturnedEmpty, setLastSuggestReturnedEmpty] =
    useState(false);

  const { suggestAddress, getAddressByPlaceId } = useContext(MpxContext);

  const requestIdRef = useRef(0);
  const latestInputValueRef = useRef("");
  const isMountedRef = useRef(true);
  const debouncedSuggestionsRef = useRef<
    DebouncedFunc<(value: string) => void> | undefined
  >(undefined);

  const debouncedFn = useMemo(
    () =>
      debounce(async (value: string) => {
        const requestId = ++requestIdRef.current;

        try {
          const suggestions = await suggestAddress(value);

          const isLatestRequest = requestId === requestIdRef.current;
          const isMounted = isMountedRef.current;
          const matchesCurrentInput = value === latestInputValueRef.current;
          const willApply = isLatestRequest && isMounted && matchesCurrentInput;

          if (willApply) {
            const list = suggestions ?? [];
            setResults(list);
            setLastSuggestReturnedEmpty(list.length === 0);
          }
        } catch {
          const isLatest = requestId === requestIdRef.current;
          const isMounted = isMountedRef.current;
          const matchesCurrentInput = value === latestInputValueRef.current;
          const willApply = isLatest && isMounted && matchesCurrentInput;

          if (willApply) {
            setResults([]);
            setLastSuggestReturnedEmpty(true);
          }
        } finally {
          const isLatest = requestId === requestIdRef.current;
          const isMounted = isMountedRef.current;
          const matchesCurrentInput = value === latestInputValueRef.current;
          if (isLatest && isMounted && matchesCurrentInput) {
            setSuggestionsLoading(false);
          }
        }
      }, DEBOUNCE_DELAY),
    [suggestAddress]
  );

  debouncedSuggestionsRef.current = debouncedFn;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      debouncedFn.cancel();
    };
  }, [suggestAddress, debouncedFn]);

  const fetchSuggestions = useCallback((value: string) => {
    latestInputValueRef.current = value;
    if (value.trim().length === 0) {
      setResults([]);
      setSuggestionsLoading(false);
      setLastSuggestReturnedEmpty(false);
      return;
    }
    setSuggestionsLoading(true);
    debouncedSuggestionsRef.current?.(value);
  }, []);

  const getPlace = useCallback(
    async (placeId: string): Promise<GeocodedAddress | undefined> => {
      if (!placeId) return undefined;

      try {
        const address = await getAddressByPlaceId(placeId);

        if (address) {
          setSelectedAddress(address);
        }
        return address;
      } catch {
        console.error("Unable to fetch address");
        return undefined;
      }
    },
    [getAddressByPlaceId]
  );

  const clearResults = useCallback(() => {
    setResults([]);
    setSuggestionsLoading(false);
    setLastSuggestReturnedEmpty(false);
  }, []);

  return {
    results,
    suggestionsLoading,
    lastSuggestReturnedEmpty,
    selectedAddress,
    setSelectedAddress,
    clearResults,
    fetchSuggestions,
    getPlace,
  };
}
